import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { NotificationCreateType } from 'webapp/enums/notifications';

import joinClasses from 'webapp/helpers/join-classes';

import BoardShare from 'webapp/models-custom/board-share';
import Reminder from 'webapp/models-custom/reminder';
import ReportScheduleSetting from 'webapp/models-custom/report-schedule-setting';

import type { ModelWithNotificationForPopup } from 'webapp/services/model-manager/notifications';

interface Args {
	model: ModelWithNotificationForPopup;
	isDuplicateFlow: boolean;
	isFromCreateNewSpecific: boolean;
	changeTab: (type: NotificationCreateType) => void;
}

export default class NotificationsEditNavigationComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	get isEdit() {
		return !this.args.model.isNew;
	}

	get showTabs() {
		// Show tabs only when we have come here from generic create
		// and there is actually something to switch to (e.g. databoards have a single type)
		return (
			!this.isEdit &&
			!this.args.isDuplicateFlow &&
			!this.args.isFromCreateNewSpecific &&
			this.tabsForType.length > 1
		);
	}

	get shownTabs() {
		return this.showTabs ? this.tabsForType : [];
	}

	@cached
	get tabsForType() {
		const types: NotificationCreateType[] = [];
		const model = this.args.model;
		if (model instanceof BoardShare) {
			types.push(NotificationCreateType.BOARD);
		} else if (model instanceof ReportScheduleSetting || model instanceof Reminder) {
			types.push(NotificationCreateType.REPORT, NotificationCreateType.REMINDER);
		} else {
			types.push(
				NotificationCreateType.METRIC,
				NotificationCreateType.SCORECARD,
				NotificationCreateType.SMART_ALERT,
				NotificationCreateType.ALERT,
			);
		}
		const selectedCreateType = this.selectedCreateType;
		return types
			.filter((type) => {
				// Only one smart alert per metric setup: hide the tab when the metric
				// already has one (unless it's the current tab, to not kick the user out).
				if (
					type === NotificationCreateType.SMART_ALERT &&
					type !== selectedCreateType &&
					this.mmNotificationsService.metricSetupHasExistingSmartAlert(model)
				) {
					return false;
				}
				return true;
			})
			.map((type) => {
				return {
					id: type,
					text: this.mmNotificationsService.getNameForCreateType(type),
					isSelected: type === selectedCreateType,
					limitation: this.mmNotificationsService.checkLimitationForCreateType(type),
				};
			});
	}

	get selectedCreateType(): NotificationCreateType {
		return this.mmNotificationsService.getCreateTypeForModel(this.args.model)!;
	}

	@action
	changeTab(type: NotificationCreateType) {
		if (this.selectedCreateType === type) {
			return;
		}
		const tab = this.shownTabs.find((t) => t.id === type);
		if (!tab) {
			return;
		}
		if (tab.limitation) {
			this.billingService.handleLimitations(tab.limitation);
			return;
		}

		this.args.changeTab(type);
	}

	<template>
		{{#if this.showTabs}}
			<ul class="modalTabs modalTabs-2">
				{{#each this.shownTabs as |tab|}}
					<li
						class={{joinClasses
							(array
								"navigation-button"
								(if tab.isSelected "navigation-button--selected")
							)
						}}
					>
						<a role="button" {{on "click" (fn this.changeTab tab.id)}}>{{tab.text}}</a>
					</li>
				{{/each}}
			</ul>
		{{/if}}
	</template>
}
